import type { RouteExample } from "@mailhand/core/schemas";
import { useApi } from "../../app/services.js";
import { useTenant } from "../../app/tenant.js";
import { useTask } from "../../app/useTask.js";
import { useResource } from "../../lib/useResource.js";
import { Button, Card, CardHeader, ErrorNote, Spinner } from "../../ui/index.js";

export function ExamplesSection() {
  const api = useApi();
  const { actions } = useTenant();
  const task = useTask();
  const { resource, reload } = useResource<RouteExample[]>(async () => (await api.call("examplesList", {})).examples, [api]);
  const nameOf = (actionId: string): string => actions.find((action) => action.id === actionId)?.name ?? actionId;

  async function remove(index: number): Promise<void> {
    await task.run(async () => {
      await api.call("exampleDelete", { params: { index: String(index) } });
      await reload();
    }, "Example deleted.");
  }

  return (
    <Card id="examples">
      <CardHeader eyebrow="Examples" />
      <p className="small muted">Each time you change an action on a message, it becomes an example the router reads.</p>
      {resource.status === "loading" ? <Spinner /> : null}
      {resource.status === "error" ? <ErrorNote message={resource.message} onRetry={() => void reload()} /> : null}
      {resource.status === "ready" ? (
        resource.data.length === 0 ? (
          <p className="muted small">No examples yet.</p>
        ) : (
          <ul className="rows">
            {resource.data.map((example, index) => (
              <li key={`${index}-${example.actionId}`} className="row">
                <span className="row-main">
                  <span className="clamp-2">{example.text}</span>
                  <span className="small">→ {nameOf(example.actionId)}</span>
                </span>
                <Button size="sm" variant="ghost" disabled={task.busy} onClick={() => void remove(index)}>
                  Delete
                </Button>
              </li>
            ))}
          </ul>
        )
      ) : null}
    </Card>
  );
}
